import React from 'react';
import { getImageURL } from '../helpers/ImageHelper';
import { IconAlertTriangleFilled, IconCarrot } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useTheme } from '../contexts/ThemeContext';


const POSMenuItemDetailedView = ({ menuItems, selectedCategory, categories, searchQuery, currency, stockSellingPolicy = 'allow_negative', btnOpenVariantAndAddonModal, addItemToCart }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();

  const filteredMenuItems = menuItems.filter((menuItem) => menuItem && menuItem.is_enabled !== false && menuItem.is_enabled !== 0 && menuItem.is_enabled !== '0')
    .filter((menuItem) => {
      const itemCatId = (typeof menuItem.category === 'object' && menuItem.category?.id) 
        ? menuItem.category.id 
        : (menuItem.category_id != null ? menuItem.category_id : menuItem.category);

      if (selectedCategory === "all" || !selectedCategory) {
        if (!itemCatId || !categories || categories.length === 0) return true;
        const matchedCat = categories.find(category => String(category.id) === String(itemCatId));
        return !matchedCat || (matchedCat.is_enabled !== false && matchedCat.is_enabled !== 0 && matchedCat.is_enabled !== '0');
      }
      return String(selectedCategory) === String(itemCatId);
    })
    .filter((menuItem) => {
      if (!searchQuery) {
        return true;
      }
      return menuItem.title.trim().toLowerCase().includes(searchQuery.trim().toLowerCase());
    });

  return (
    <div className='w-full h-full overflow-hidden'>
      {filteredMenuItems.length === 0 ? (
        <div className="flex flex-col justify-center items-center w-full h-full">
          <img src="/assets/illustrations/pos-not-found.webp" alt={t('pos.not_found_img_alt')} className="w-1/4 mb-4" />
          <p className="text-lg text-restro-green font-bold">{t("pos_menu.not_found_title")}</p>
          <p className="text-gray-500">{t("pos_menu.not_found_message")}</p>
          <p className="text-gray-500">{t("pos_menu.not_found_carrot")}</p>
        </div>
      ) : (
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-4 w-full z-0 px-4 pb-4 rounded-b-2xl overflow-y-auto h-full content-start '>
          {filteredMenuItems.map((menuItem, i) => {
            const { title, description, id, price, image, category_id, category_title, addons, variants } = menuItem;

            const imageURL = image ? getImageURL(image) : null;
            const hasVariantOrAddon = variants?.length > 0 || addons?.length > 0;

            const baseRecipeItems = menuItem.recipeItems?.filter(
              (r) => r.variant_id === 0 && r.addon_id === 0
            );

            const isOutOfStock = baseRecipeItems?.length > 0 && baseRecipeItems.some(
              (r) => parseFloat(String(String(r.current_quantity || "0").replace(/,/g, "")).replace(/,/g, "")) < parseFloat(String(String(r.recipe_quantity || "1").replace(/,/g, "")).replace(/,/g, ""))
            );

            const isLowStock = !isOutOfStock && baseRecipeItems?.some(
              (r) => parseFloat(String(String(r.current_quantity || "0").replace(/,/g, "")).replace(/,/g, "")) <= parseFloat(String(String(r.min_quantity_threshold || "0").replace(/,/g, "")).replace(/,/g, ""))
            );

            const quantitiesPossible = baseRecipeItems?.map(r => {
              const currentQty = parseFloat(String(String(r.current_quantity || "0").replace(/,/g, "")).replace(/,/g, ""));
              const requiredQty = parseFloat(String(String(r.recipe_quantity || "1").replace(/,/g, "")).replace(/,/g, ""));
              return Math.floor(currentQty / requiredQty);
            });

            const minItemsCanBeMade = quantitiesPossible?.length > 0
              ? Math.min(...quantitiesPossible)
              : null;

            const isBlocked = stockSellingPolicy === 'block' && isOutOfStock && !hasVariantOrAddon;

            return (
              <div className={`flex gap-2 min-h-32 h-full min-h-36 border rounded-2xl border-restro-border-green text-restro-text ${isBlocked ? 'opacity-70 bg-gray-50/50' : ''}`} key={i}>
                <div>
                  <div className='w-28 flex-shrink-0 h-full hidden md:flex items-center justify-center text-gray-300 relative rounded-l-2xl text-restro-text bg-restro-gray'>
                    {image ? <img src={imageURL} alt={title} loading="lazy" className="w-full h-full absolute top-0 left-0 rounded-l-2xl object-cover " /> : <IconCarrot />}
                    {category_title && (
                      <div className="absolute top-0 left-0 bg-restro-green-dark text-white text-xs font-semibold px-2 py-1 rounded-tl-xl rounded-br-lg">
                        {category_title}
                      </div>
                    )}
                    {isOutOfStock ? (
                      <div className="absolute left-0 bottom-0 bg-red-600 text-white text-[10px] font-bold px-1 py-[2px] z-10 w-full flex items-center justify-center gap-1 rounded-bl-2xl">
                        <IconAlertTriangleFilled size={12} />
                        <span>{t('inventory.status_out_of_stock') || 'Out of Stock'}</span>
                      </div>
                    ) : isLowStock && (
                      <div className="absolute left-0 bottom-0 bg-amber-50 text-amber-600 text-[10px] font-medium px-1 py-[1px] z-10 w-full flex flex-col items-center gap-[2px] rounded-bl-2xl">
                        <div className="flex items-center gap-1">
                          <IconAlertTriangleFilled size={12} />
                          <span>Low Stock - {minItemsCanBeMade} Qty</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="p-2 flex flex-col justify-between gap-2 w-full">
                  <div>
                    <div className='flex justify-between gap-4 mr-1'>
                      <p className='text-sm font-semibold flex-1 break-words line-clamp-3'>{title}</p>
                      <p className='text-restro-green font-bold'>{currency}{price}</p>
                    </div>
                    <p className='line-clamp-2 text-ellipsis text-xs text-gray-500 mt-0.5'>{description}</p>
                  </div>
                  <div className="flex justify-between items-end gap-4 w-full">
                    <p className="text-xs text-gray-500">{variants?.length > 0 && <span>{variants?.length} {t("pos_menu.variants")}</span>} {addons?.length > 0 && <span>{addons?.length} {t("pos_menu.addons")}</span>}</p>
                    <button
                      onClick={() => {
                        if (isBlocked) {
                          alert(t('inventory.insufficient_stock_message_pos') || 'Out of stock: Cannot sell this item');
                          return;
                        }
                        if (hasVariantOrAddon) {
                          btnOpenVariantAndAddonModal(id);
                        } else {
                          addItemToCart(menuItem);
                        }
                      }}
                      className={`rounded-lg px-6 py-1 transition active:scale-95 text-white flex items-center justify-center font-bold ${
                        isBlocked ? 'bg-gray-400 opacity-60 cursor-not-allowed hover:bg-gray-400' : 'bg-restro-green hover:bg-restro-green-button-hover'
                      }`}
                    >
                      {isBlocked ? (t('inventory.status_out_of_stock') || 'Out of Stock') : t("pos_menu.add")}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          <div className="h-40"></div>
        </div>
      )}
    </div>
  );
};

export default POSMenuItemDetailedView;
